import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { FormError } from '../../../shared/configs/form-error.config';
import { CustomError } from '../../../shared/models/api';

@Injectable({
  providedIn: 'root',
})
export class ValidationService {
  readonly abhaNumber = /^\d{2}-\d{4}-\d{4}-\d{4}$/;
  readonly aadhaarNumber = /^\d{4}\s\d{4}\s\d{4}$/;
  readonly atleastOneCharacterRegex = /\S+/;
  readonly characterOnlyRegex = /^[^\d]*$/;
  readonly contactNumberRegex = /^\d{10}$/;
  readonly emailRegex = /^([a-zA-Z0-9_.-]+)@([\da-zA-Z.-]+)\.([a-zA-Z.]{2,6})$/;
  readonly floatingNumberRegex = /^[-+]?[0-9]*.?[0-9]+([eE][-+]?[0-9]+)?$/;
  readonly numberRegex = /^\d+$/;
  readonly zeroToHundredDecimalRegex = /^(?:100(?:\.0{1,3})?|\d{1,2}(?:\.\d{1,3})?)$/;
  readonly decimalNumberRegex = /^-?\d+(\.\d+)?$/;
  readonly twoDigitDecimalNumberRegex = /^\d+(\.\d{0,2})?$/;
  readonly tenDigitsBeforeDecimal = /^(?:\d{1,10}(?:\.\d{1,})?|\d{10}(?:\.\d{1,})?)\.?$/;
  readonly heightRegex = /^(0?\d(\.\d+)?|10(\.0+)?):(0?\d|1[0-2])(?:\.\d+)?$/;
  readonly greaterThanZeroNumberRegex = /^[1-9][0-9]*$/;
  readonly panRegex = /^([A-Z]){5}([0-9]){4}([A-Z]){1}$/;
  readonly passwordRegex = /.{6}/;
  readonly personNameRegex = /^[a-zA-Z _.'-]*$/;
  readonly roleNameRegex = /^[A-Za-z0-9_]+$/;
  readonly pincodeRegex = /^[0-9]\d{5}$/;
  readonly salaryRangeRegex = /^[1-9]+\d*(-[1-9]+\d*)?$/;
  readonly specialCharacterRegex = /^[a-zA-Z0-9 /()_.@:-]*$/;
  readonly specialCharacterTextareaRegex = /^[a-zA-Z0-9\n /()_.@:-]*$/;
  readonly urlRegex = /(?:https?:\/\/|www\.)([a-z0-9-]+\.)+[a-z]{2,4}(\.[a-z]{2,4})*(\/[^ ]+)*/i;
  readonly numberPosNegRegex = /^-?[0-9]+(?:\.[0-9]+)?$/;
  readonly bankAccountNumberRegex = /^[0-9]{9,18}$/;
  readonly bankIFSCRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  readonly percentageRegex = /(^100(\.0{1,2})?$)|(^([1-9]([0-9])?|0)(\.[0-9]{1,2})?$)/;
  readonly weightRegex = /^(?:[0-6]?\d{0,2}(?:\.\d+)?|700(?:\.0+)?|\.[1-9]\d{0,2})$/;
  readonly monthYearRegex = /^\d+\.([1-9]|1[0-1])$/;
  readonly conditionDurationYear = /^(?!0+(\.0*)?$)(\d{1,2}|100)(\.(0?[0-9]|1[0-2]))?$/;
  readonly packageIdRegex = /^[A-Za-z]+-[0-9]+$/;
  readonly customerCareNumberRegex = /^[\d\s-]+$/;

  /** For adding unprocessable entity errors returned from BE directly to form
   * @param form - form group to which the error must be attached
   * @param error - error to be attached
   */
  addResponseErrorToForm(form: FormGroup, error: CustomError): void {
    if (error.statusCode !== HttpStatusCode.UnprocessableEntity) {
      return;
    }

    Object.entries(error.errors).forEach(([fieldName, errors]) => {
      form.get(fieldName)?.setErrors({
        [FormError.responseError]: errors[0],
      });
      form.get(fieldName)?.markAsDirty();
    });
  }

  /** For adding required validator based on a condition (usually other controls)
   * @param condition - function which returns the condition to be checked for adding validator
   */
  conditionalRequiredValidator(condition: () => boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.parent == null) {
        return null;
      }

      return condition() === true ? Validators.required(control) : null;
    };
  }

  /**For showing custom decimal error for pattern validation
   *
   * @param decimals number of decimal places required
   * @returns regex
   */
  getDecimalRegex(decimals: number): RegExp {
    return new RegExp(`^-?\\d+(\\.\\d{1, ${decimals}})?$`);
  }

  /** For getting error messages corresponding to each error. Returns translation key and options.
   * @param errorName - name of error
   * @param errorValue - value of the error obtained from form control
   * @param fieldName - name of the field to be shown in message
   */
  getMessage(errorName: string, errorValue: any, fieldName: string): [string, Record<string, any> | undefined] {
    const translationKey = 'validation';

    const errorMessages: any = {
      [FormError.aadhaarNumber]: [`${translationKey}.aadhaarNumber`],
      [FormError.contactNumber]: [`${translationKey}.contactNumber`, { fieldName }],
      [FormError.maxLength]: [`${translationKey}.maxLength`, { requiredLength: errorValue.requiredLength }],
      [FormError.onlyAlphabets]: [`${translationKey}.onlyAlphabets`, { fieldName }],
      [FormError.password]: [`${translationKey}.password`],
      [FormError.pattern]: [`${translationKey}.pattern`, { fieldName }],
      [FormError.required]: [`${translationKey}.required`, { fieldName }],
      [FormError.responseError]: [`${errorValue}`],
    };

    return errorMessages[errorName];
  }

  /** For showing custom error for pattern validation
   * @param regex - regex to be used for pattern validation
   * @param error - error to be thrown
   */
  regexValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value == null || control.value === '') {
        return null;
      }

      return regex.test(control.value) ? null : error;
    };
  }

  regexMobileNoValidation(count: number): RegExp {
    return new RegExp(`^\\d{${count}}$`);
  }

  getWeightEntryDecimalRegex(decimals: number): RegExp {
    return new RegExp(`^-?\\d+(\\.\\d{1,${decimals}})?$`);
  }
}
